import React, {useState, useEffect, useCallback} from 'react';
import {Modal, Text, TextInput, SelectInput, Button} from '@/ui-kits';
import {SelectInputitem} from '@/interface';
import { ratingFilterLabelRegex } from '@/constants';

interface AddRatingModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    label: string;
    from: string;
    to: string;
    status: boolean;
  }) => void;
}

const statusOptions: SelectInputitem[] = [
  {id: 1, label: 'Active'},
  {id: 2, label: 'Inactive'},
];

export const AddRatingModal: React.FC<AddRatingModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [label, setLabel] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState<SelectInputitem | null>(statusOptions[0]);
  const [error, setError] = useState({
    from: '',
    to: '',
  });
  // Helper function to validate and format decimal input
  const handleDecimalInput = (value: string): string => {
    // Allow empty string
    if (value === '') return '';
    
    // Remove any characters that aren't digits or decimal point
    let cleaned = value.replace(/[^\d.]/g, '');
    
    // Only allow one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    
    return cleaned;
  };

  useEffect(() => {
    if (open) {
      // Reset form when modal opens
      setLabel('');
      setFrom('');
      setTo('');
      setStatus(statusOptions[0]);
    }
  }, [open]);

  const isDirty = Boolean(label.trim() || from.trim() || to.trim());

  const handleSave = () => {
    if (!label || !from || !to || !status) return;
    
    onSave({
      label,
      from,
      to,
      status: status.label === 'Active',
    });
    
    // Reset form
    setLabel('');
    setFrom('');
    setTo('');
    setStatus(statusOptions[0]);
    onClose();
  };

  const isFormValid = label && from && to && status && !error.from && !error.to;


  useEffect(() => {

    if (from) {
      if (Number.parseFloat(from) >= 5) {
        setError({...error, from: 'From must be less than 5'});
      } else {
        setError({...error, from: ''});
      }
    }
    if (to) {
      if (Number.parseFloat(to) > 5) {
        setError({...error, to: 'To must be less than or equal to 5'});
      } else {
        setError({...error, to: ''});
      }
    }
  }, [from, to]);

  const closeModal = useCallback(() => {
    if (!isDirty) {
      onClose();
    }
  }, [isDirty, onClose]);

  return (
    <Modal
      open={open}
      onClose={closeModal}
      className="relative bg-white py-6 px-8 rounded-xl gap-4 w-[90vw] max-w-[500px]">
      <Text variant="free" className="text-ui_blue font-FigtreeBold text-3xl xl mb-4">
        Add Ratings
      </Text>

      <div className="flex flex-col gap-4">
        {/* Label Name */}
        <TextInput
          allowedRegex={ratingFilterLabelRegex}
          label="Label"
          value={label}
          onChangeText={(value) => setLabel(value)}
          placeholder="Enter Label"
          className="w-full"
          required
        />

        {/* From */}
        <TextInput
          label="From"
          type="decimal"
          value={from}
          onChangeText={(value) => setFrom(handleDecimalInput(value))}
          placeholder="Enter Rating"
          className="w-full"
          required
          error={error.from}
          touched={from !== ''}
        />

        {/* To */}
        <TextInput
          label="To"
          type="decimal"
          value={to}
          onChangeText={(value) => setTo(handleDecimalInput(value))}
          placeholder="Enter Rating"
          className="w-full"
          required
          error={error.to}
          touched={to !== ''}
        />

        {/* Status */}
        <SelectInput
          label="Status"
          value={status?.id || null}
          options={statusOptions}
          onChange={(item) => setStatus(item)}
          placeholder="Select Status"
          className="w-full"
          required
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 mt-6">
        <Button
          variant="secondary"
          title="Cancel"
          onClick={onClose}
          className="flex-1"
        />
        <Button
          variant="primary"
          title="Save"
          onClick={handleSave}
          disabled={!isFormValid}
          className="flex-1"
        />
      </div>
    </Modal>
  );
};

